// @flow

import React, { type Node, useCallback } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import styled from 'styled-components';
import { Separator, RadioButton } from '@tectonics/look-and-feel/volcanes/atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import './upi-intent.css';
import { getAppImageSrc } from './get-app-image-src';

export function FieldIndiaUpiApp(upiAppInfos: any): Node {
  const inputId = useUniqueId();
  const validationError = useUserInputValidationError('indiaUpiApp');
  const formHasBeenSent = useFormHasBeenSent();
  const [indiaUpiApp, setIndiaUpiApp] = useUserInputValue('indiaUpiApp');
  const appLinks = upiAppInfos.upiAppLinks;
  const linkStyle = {
    textDecoration: 'none',
    cursor: 'pointer',
    color: '#121419',
  };
  const otherAppName = 'other-app';

  const handleClick = useCallback((appName) => {
    sessionStorage.setItem('appName', appName);
    setIndiaUpiApp(appName);
  }, [setIndiaUpiApp]);

  return (
    <Root>
      <Body>
        <Title>Please, select your UPI APP:</Title>
        <Separator direction="horizontal" size={7} />
        <div data-test-name="upi-intent-app">
          <Apps>
            {appLinks.map((appName, index) => {

              return (
                <Label key={index} htmlFor={`${inputId}-${appName}`}>
                  <RadioButton
                    id={`${inputId}-${appName}`}
                    name="upi-intent-app"
                    value={appName}
                    data-test-value={appName}
                    style={linkStyle}
                    onClick={() => handleClick(appName)}
                    checked={appName === indiaUpiApp}
                  />
                  <Separator direction="horizontal" size={3} />
                  <img src={getAppImageSrc(appName)} alt={appName} />
                  <Separator direction="horizontal" size={3} />
                </Label>
              );
            })}
          </Apps>
          <Separator direction="vertical" size={4} />
        </div>

        {formHasBeenSent && validationError && (
          <ValidationMessage validationError={validationError} />
        )}

        <InfoBody>
          <Info>
            <span
              className={otherAppName === indiaUpiApp ? 'chosen-app' : ''}
              style={linkStyle}
              onClick={() => handleClick(otherAppName)}>
              Choose other UPI APP
            </span>
          </Info>
        </InfoBody>
        <Separator direction="vertical" size={3} />
        <SubTitle>Processing may take a few minutes. Please do not refresh or use the back button to avoid transaction issues</SubTitle>
      </Body>
    </Root>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          Please, select your UPI APP
        </ErrorLabel>
      );
  }
}

const Root = styled.div`
  color: ${props => props.theme.colors.gray(20)};
`;

const Body = styled.div`
  padding: 32px 0;
`;

const Title = styled.h1`
  text-align: center;
  width: Fixed (342px);
  height: Hug (21px);
`;

const SubTitle = styled.h1`
  text-align: center;
  width: Fixed (200px);
  height: Hug (10px);
`;

const Apps = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const InfoBody = styled.div`
  background: ${props => props.theme.colors.new_primary[50]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const Info = styled.div`
  text-align: center;
  font-size: 13px;
  line-height: 21px;
  color: ${props => props.theme.colors.neutral[900]};
  width: 100%;
  display: inline-block;
  font-family: Noto Sans, "Open Sans", "OpenSans", Arial, "Helvetica Neue", Helvetica, sans-serif;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 16px;
`;
